import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { formatEdges } from "../utils/graphFormatters";

const baseURL = import.meta.env.VITE_Backend_URL;

// Helper function to handle 401 unauthorized errors
const handleUnauthorized = () => {
  // Clear token from localStorage
  localStorage.removeItem('lsh-token');
  // Redirect to login page
  window.location.href = '/';
};

// Base query with 401 error handling
const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("lsh-token");
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  })(args, api, extraOptions);

  // Check for 401 unauthorized error
  if (result.error && result.error.status === 401) {
    handleUnauthorized();
  }

  return result;
};

export const organizationApi = createApi({
  reducerPath: "organizationApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Hierarchy", "Organization", "MapOrganizations", "Group"],
  endpoints: (builder) => ({
    getHierarchy: builder.query({
      async queryFn(_arg, _queryApi, _extra, fetchWithBQ) {
        const [orgsResponse, edgesResponse] = await Promise.all([
          fetchWithBQ("/api/oragnization/hierarchie"),
          fetchWithBQ("/api/oragnization/edges"),
        ]);

        // Check for 401 unauthorized errors
        if (orgsResponse.error && orgsResponse.error.status === 401) {
          handleUnauthorized();
          return { error: orgsResponse.error };
        }
        if (edgesResponse.error && edgesResponse.error.status === 401) {
          handleUnauthorized();
          return { error: edgesResponse.error };
        }

        if (orgsResponse.error) {
          return { error: orgsResponse.error };
        }
        if (edgesResponse.error) {
          return { error: edgesResponse.error };
        }
        const rawNodes = orgsResponse.data?.data || orgsResponse.data || [];
        const rawEdges = edgesResponse.data?.data || edgesResponse.data || [];
        return {
          data: {
            viewPermission: orgsResponse?.data?.viewPermission || false,
            rootId: orgsResponse.data.rootId,
            nodes: Array.isArray(rawNodes) ? rawNodes : [],
            edges:  Array.isArray(rawEdges) ? formatEdges(rawEdges) : [],
          },
        };
      },
      providesTags: (result) => {
        if (!result?.nodes?.length) {
          return [{ type: "Hierarchy", id: "LIST" }];
        }
        return [
          ...result.nodes.map(({ id }) => ({ type: "Organization", id })),
          { type: "Hierarchy", id: "LIST" },
        ];
      },
    }),
    updatePositions: builder.mutation({
      query: (payload) => ({
        url: "/api/oragnization/updates",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [{ type: "Hierarchy", id: "LIST" }],
    }),
    createOrganization: builder.mutation({
      query: (payload) => ({
        url: "/api/oragnization",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Hierarchy", id: "LIST" }],
    }),
    createBranch: builder.mutation({
      query: (payload) => ({
        url: "/api/oragnization/branch",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Hierarchy", id: "LIST" }],
    }),
    addEdgeToParent: builder.mutation({
      query: (payload) => ({
        url: "/api/oragnization/edges",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [{ type: "Hierarchy", id: "LIST" }],
    }),
    updateOrganization: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/oragnization/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Organization", id },
        { type: "Hierarchy", id: "LIST" },
      ],
    }),
    deleteOrganization: builder.mutation({
      query: ({ id, isRoot }) => ({
        url: `/api/oragnization/${id}`,
        method: "DELETE",
        params: { isRoot },
      }),
      invalidatesTags: [{ type: "Hierarchy", id: "LIST" }],
    }),
    getMapOrganizations: builder.query({
      query: () => "/api/oragnization/map",
      providesTags: ["MapOrganizations"],
      transformResponse: (response) => {
        if (response.success && response.data) {
          return response.data.map((org) => ({
            _id: org._id,
            companyName: org.companyName,
            isBranch: org.isBranch || false,
            location: org?.placelocation || org.location,
            data: {
              companyName: org.data?.companyName || org.companyName,
              jointcompany: org.data?.jointcompany || org.jointcompany || false,
              location: org.data?.location || org.location,
            },
          }));
        }
        return [];
      },
    }),
    getOrganizationById: builder.query({
      query: (id) => `/api/oragnization/${id}`,
      providesTags: (result, error, id) => [{ type: "Organization", id }],
      transformResponse: (response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return null;
      },
    }),
    getGroups: builder.query({
      query: () => "/api/group/getallbusiness",
      providesTags: (result) =>
        result?.length
          ? [
              ...result.map(({ _id }) => ({ type: "Group", id: _id })),
              { type: "Group", id: "LIST" },
            ]
          : [{ type: "Group", id: "LIST" }],
    }),
    createGroup: builder.mutation({
      query: (payload) => ({
        url: "/api/group/createbusiness",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Group", id: "LIST" }],
    }),
    deleteGroup: builder.mutation({
      query: (id) => ({
        url: `/api/group/deletebusiness/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Group", id },
        { type: "Group", id: "LIST" },
      ],
    }),
    updateGroup: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/group/updatebusiness/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Group", id },
        { type: "Group", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetHierarchyQuery,
  useUpdatePositionsMutation,
  useCreateOrganizationMutation,
  useCreateBranchMutation,
  useAddEdgeToParentMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetMapOrganizationsQuery,
  useLazyGetOrganizationByIdQuery,
  useGetGroupsQuery,
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useUpdateGroupMutation,
} = organizationApi;
